import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "semantic-ui-react";
// Actions for data loading only
import { GET_EMPLOYEES, SG_GET_SURVEY_STRUCTURES } from "constants/actions";
import { SurveyProvider, useSurveyContext } from "./Data/SurveyFormContext";
import { UserSearchProvider } from "Providers/UserSearchProvider";

const buildDefaultFrenchEmailTemplate = ({
  organizationNameFr,
  organizationNameEn,
  surveyName,
}) => {
  const organizationDisplay =
    (organizationNameFr && organizationNameFr.trim()) ||
    (organizationNameEn && organizationNameEn.trim()) ||
    "notre organisation";
  const surveyDisplay =
    (surveyName && surveyName.trim()) || "ce sondage éclair";

  return (
    `Bonjour,\n\n` +
    `Nous vous invitons à participer à notre court sondage intitulé "${surveyDisplay}". ` +
    `Vos réponses demeureront confidentielles et nous aideront à améliorer nos initiatives.\n\n` +
    `Merci de prendre quelques minutes pour nous partager votre perspective.\n\n` +
    `Merci,\n${organizationDisplay}`
  );
};

// 3-step wizard for Pulse Survey (OTP): Details → Recipients → Email
// No question editing. No left-side menu. No new_components.
const SurveyFormPageInner = ({ selectedPulse }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const { formData, updateFormData, handleSubmit, loading } = useSurveyContext();

  const [currentStep, setCurrentStep] = useState(1);
  const [sportModalOpen, setSportModalOpen] = useState(false);
  const [sportSearchTerm, setSportSearchTerm] = useState("");

  // Step 1: Basic details
  const [details, setDetails] = useState({
    name: "",
    description: "",
    orgNameEn: "",
    orgNameFr: "",
  });

  // Step 2: Recipients selection (modeled after AddRecipientsModal)
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("individual"); // individual | category
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Step 3: Email content
  const [emailContent, setEmailContent] = useState({
    subjectEn: "",
    bodyEn: "",
    bodyFr: "",
  });

  // Redux state
  const { contacts, contactCategories, surveyStructure, auth } = useSelector(
    (state) => ({
      contacts: Array.isArray(state.employees?.employees)
        ? state.employees.employees
        : [],
      contactCategories: Array.isArray(state.employee_category?.employee_org_category)
        ? state.employee_category.employee_org_category
        : [],
      surveyStructure: state.surveystructure?.survey_structure || [],
      auth: state.auth || {},
    }),
    shallowEqual
  );

  const selectedSport = formData?.selectedSport || null;

  const sportSubfactorStructure = useMemo(() => {
    const categories = surveyStructure?.[0]?.categories || [];
    return categories
      .filter((category) => category.priority === "primary")
      .map((primaryCategory) => ({
        primaryId: primaryCategory.id,
        primaryName: primaryCategory.name,
        parents: (primaryCategory.options || [])
          .filter(
            (option) =>
              Array.isArray(option.subfactors) && option.subfactors.length > 0
          )
          .map((option) => ({
            parentOptionId: option.id,
            parentOptionName: option.name,
            subfactors: option.subfactors.map((subfactor) => ({
              id: subfactor.id,
              name: subfactor.name,
              label: subfactor.text || subfactor.name,
            })),
          })),
      }))
      .filter((primary) => primary.parents.length > 0);
  }, [surveyStructure]);

  const filteredSportStructure = useMemo(() => {
    const query = sportSearchTerm.trim().toLowerCase();
    if (!query) return sportSubfactorStructure;

    return sportSubfactorStructure
      .map((primary) => {
        const primaryMatches = primary.primaryName
          ?.toLowerCase()
          .includes(query);
        const matchedParents = primary.parents
          .map((parent) => {
            const parentMatches = parent.parentOptionName
              ?.toLowerCase()
              .includes(query);
            const matchedSubfactors = parent.subfactors.filter(
              (subfactor) =>
                subfactor.name?.toLowerCase().includes(query) ||
                subfactor.label?.toLowerCase().includes(query)
            );

            if (parentMatches && matchedSubfactors.length === 0) {
              return { ...parent };
            }

            if (matchedSubfactors.length > 0) {
              return { ...parent, subfactors: matchedSubfactors };
            }

            return null;
          })
          .filter(Boolean);

        if (primaryMatches && matchedParents.length === 0) {
          return { ...primary };
        }

        if (matchedParents.length > 0) {
          return { ...primary, parents: matchedParents };
        }

        return null;
      })
      .filter(Boolean);
  }, [sportSubfactorStructure, sportSearchTerm]);

  const handleSportSelect = (subfactor, parent, primary) => {
    const selection = {
      id: subfactor.id,
      name: subfactor.name,
      label: subfactor.label,
      primaryId: primary.primaryId,
      primaryName: primary.primaryName,
      parentOptionId: parent.parentOptionId,
      parentOptionName: parent.parentOptionName,
    };
    updateFormData({ selectedSport: selection });
    setSportSearchTerm("");
    setSportModalOpen(false);
  };

  const handleClearSport = () => {
    updateFormData({ selectedSport: null });
    setSportSearchTerm("");
  };

  const handleApplyDefaultFrenchEmail = () => {
    const template = buildDefaultFrenchEmailTemplate({
      organizationNameFr:
        details.orgNameFr?.trim() || formData.organizationNameFr || "",
      organizationNameEn:
        details.orgNameEn?.trim() || formData.organizationName || "",
      surveyName: details.name?.trim() || selectedPulse?.name || "",
    });

    setEmailContent((prev) => ({ ...prev, bodyFr: template }));
    updateFormData({ emailBodyFr: template });
  };

  // Load base data
  useEffect(() => {
    if (!contacts?.length) dispatch({ type: GET_EMPLOYEES });
  }, [dispatch, contacts?.length]);

  useEffect(() => {
    const orgId = Number(auth?.organization_id);
    if (orgId && (!surveyStructure || surveyStructure.length === 0)) {
      dispatch({ type: SG_GET_SURVEY_STRUCTURES, payload: `organization=${orgId}` });
    }
  }, [dispatch, auth?.organization_id, surveyStructure]);

  // Category filter options (visible, non-demographic)
  const filterCategories = useMemo(() => {
    if (!surveyStructure?.[0]?.categories) return [];
    const categories = surveyStructure[0].categories
      .filter((cat) => cat.visibility !== "private" && !cat.demographic);

    const primary = categories.find((c) => c.priority === "primary");
    const result = [];

    // Non-primary categories unchanged
    categories.forEach((cat) => {
      if (cat.priority !== "primary") {
        result.push({
          id: cat.id,
          name: cat.name,
          priority: cat.priority,
          options: (cat.options || []).map((opt) => ({ id: opt.id, name: opt.name, text: opt.text || opt.name })),
        });
      }
    });

    // Primary: base options and each subfactor parent as its own category
    if (primary) {
      const basePrimaryOptions = (primary.options || []).filter(
        (opt) => !Array.isArray(opt.subfactors) || opt.subfactors.length === 0
      );
      if (basePrimaryOptions.length > 0) {
        result.push({
          id: primary.id,
          name: primary.name,
          priority: primary.priority,
          options: basePrimaryOptions.map((opt) => ({ id: opt.id, name: opt.name, text: opt.text || opt.name })),
        });
      }

      (primary.options || [])
        .filter((opt) => Array.isArray(opt.subfactors) && opt.subfactors.length > 0)
        .forEach((parent) => {
          result.push({
            id: `primary-parent-${parent.id}`,
            name: parent.name,
            priority: "primary",
            options: parent.subfactors.map((sf) => ({ id: sf.id, name: sf.name, text: sf.text || sf.name })),
          });
        });
    }

    return result;
  }, [surveyStructure]);

  // Available contacts (active only)
  const availableRecipients = useMemo(() => {
    if (!Array.isArray(contacts)) return [];
    return contacts.filter((c) => c.status === 1);
  }, [contacts]);

  // Filter contacts by search or categories
  const filteredRecipients = useMemo(() => {
    let filtered = availableRecipients;

    if (selectedCategories.length > 0) {
      filtered = filtered.filter((c) => {
        const cat = contactCategories.find(
          (cc) => cc?.employee === c.id || cc?.employee_id === c.id
        );
        if (!Array.isArray(cat?.category)) return false;
        return selectedCategories.some((sel) => {
          const selKey = String(sel);
          return cat.category.some((k) => {
            if (!k) return false;
            return (
              String(k.value_id) === selKey ||
              String(k.value) === selKey
            );
          });
        });
      });
    } else if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.first_name?.toLowerCase().includes(q) ||
          c.last_name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [availableRecipients, searchTerm, selectedCategories, contactCategories]);

  const addableRecipients = useMemo(
    () => filteredRecipients.filter((r) => !selectedRecipients.includes(r.id)),
    [filteredRecipients, selectedRecipients]
  );

  // Validation & navigation
  const canContinueFromStep1 = useMemo(() => {
    return (
      details.name.trim().length > 0 &&
      details.orgNameEn.trim().length > 0 &&
      details.orgNameFr.trim().length > 0
    );
  }, [details]);

  const canContinueFromStep2 = useMemo(
    () => selectedRecipients.length > 0,
    [selectedRecipients.length]
  );

  const canSubmit = useMemo(() => {
    return (
      emailContent.subjectEn.trim().length > 0 &&
      emailContent.bodyEn.trim().length > 0 &&
      emailContent.bodyFr.trim().length > 0
    );
  }, [emailContent]);

  const goNext = () => {
    if (currentStep === 1 && !canContinueFromStep1) {
      addToast("Please complete name and both organization names (EN/FR)", { appearance: "error" });
      return;
    }
    if (currentStep === 2 && !canContinueFromStep2) {
      addToast("Please select at least one recipient", { appearance: "error" });
      return;
    }
    // Sync context with local state on step advance
    if (currentStep === 1) {
      updateFormData({
        name: details.name,
        description: details.description,
        organizationName: details.orgNameEn,
        organizationNameFr: details.orgNameFr,
      });
    }
    if (currentStep === 2) {
      const assignedUsersList = selectedRecipients.map((id) => ({ key: id }));
      updateFormData({ assignedUsersList });
    }
    setCurrentStep((s) => Math.min(3, s + 1));
  };

  const goBack = () => setCurrentStep((s) => Math.max(1, s - 1));

  // Recipients interactions
  const toggleRecipient = (id) => {
    setSelectedRecipients((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      updateFormData({ assignedUsersList: next.map((uid) => ({ key: uid })) });
      return next;
    });
  };

  const toggleCategory = (categoryOptionId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryOptionId)
        ? prev.filter((x) => x !== categoryOptionId)
        : [...prev, categoryOptionId]
    );
  };

  const toggleExpand = (id) => setExpandedCategory((prev) => (prev === id ? null : id));

  const addCategoryRecipients = () => {
    const ids = addableRecipients.map((r) => r.id);
    if (ids.length === 0) {
      setSelectedCategories([]);
      setExpandedCategory(null);
      return;
    }
    setSelectedRecipients((prev) => {
      const next = [...new Set([...prev, ...ids])];
      updateFormData({ assignedUsersList: next.map((uid) => ({ key: uid })) });
      return next;
    });
    setSelectedCategories([]);
    setExpandedCategory(null);
  };

  const clearAllRecipients = () => {
    setSelectedRecipients([]);
    setSelectedCategories([]);
    setExpandedCategory(null);
    updateFormData({ assignedUsersList: [] });
  };

  // Submit: trigger send using context (context is synced on change)
  const onSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      addToast("Please complete English subject/body and French body", { appearance: "error" });
      return;
    }
    handleSubmit();
  };

  return (
    <Container>
      <Breadcrumbs>
        <CrumbLink onClick={() => navigate("/app/otp/AI/pulse-survey")}>Pulse Surveys</CrumbLink>
        <CrumbSeparator>/</CrumbSeparator>
        <CrumbCurrent>Create</CrumbCurrent>
      </Breadcrumbs>
      <Header>
        <HeaderText>
          <Title>New Pulse Survey</Title>
          <Subtitle>Three steps — Details, Recipients, Email</Subtitle>
        </HeaderText>
        <Progress>
          <Step active={currentStep === 1} completed={currentStep > 1}>
            <StepNum active={currentStep === 1} completed={currentStep > 1}>1</StepNum>
            <StepText>Details</StepText>
          </Step>
          <Connector completed={currentStep > 1} />
          <Step active={currentStep === 2} completed={currentStep > 2}>
            <StepNum active={currentStep === 2} completed={currentStep > 2}>2</StepNum>
            <StepText>Recipients</StepText>
          </Step>
          <Connector completed={currentStep > 2} />
          <Step active={currentStep === 3}>
            <StepNum active={currentStep === 3}>3</StepNum>
            <StepText>Email</StepText>
          </Step>
        </Progress>
      </Header>

      <Card>
        <form onSubmit={onSubmit}>
          {currentStep === 1 && (
            <Body>
              {selectedPulse ? (
                <PulseInfo>
                  <PulseTitle>Selected Pulse: {selectedPulse.name || "Untitled"}</PulseTitle>
                  {selectedPulse.description ? (
                    <PulseDesc>{selectedPulse.description}</PulseDesc>
                  ) : null}
                </PulseInfo>
              ) : null}
              <Section>
                <SectionTitle>Basic Information</SectionTitle>
                <Field>
                  <Label>Survey Name *</Label>
                  <Input
                    type="text"
                    value={details.name}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDetails((p) => ({ ...p, name: v }));
                      updateFormData({ name: v });
                    }}
                    placeholder="e.g., Monthly Pulse — October"
                  />
                </Field>
                <Field>
                  <Label>Description</Label>
                  <TextArea
                    rows={4}
                    value={details.description}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDetails((p) => ({ ...p, description: v }));
                      updateFormData({ description: v });
                    }}
                    placeholder="Short internal description (optional)"
                  />
                </Field>
              </Section>

              <Section>
                <SectionTitle>Organization Names</SectionTitle>
                <TwoCols>
                  <Field>
                    <Label>Organization Name (English) *</Label>
                    <Input
                      type="text"
                      value={details.orgNameEn}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDetails((p) => ({ ...p, orgNameEn: v }));
                        updateFormData({ organizationName: v });
                      }}
                      placeholder="Innerlogic"
                    />
                  </Field>
                  <Field>
                    <Label>Organization Name (French) *</Label>
                    <Input
                      type="text"
                      value={details.orgNameFr}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDetails((p) => ({ ...p, orgNameFr: v }));
                        updateFormData({ organizationNameFr: v });
                      }}
                      placeholder="Innerlogic (FR)"
                    />
                  </Field>
                </TwoCols>
              </Section>

              <Section>
                <SectionTitle>Sport Selection</SectionTitle>
                <SportSelectionWrapper>
                  <SportSelectionInfo>
                    {selectedSport ? (
                      <>
                        <SelectedSportBadge>
                          <SelectedSportName>{selectedSport.name}</SelectedSportName>
                          <SelectedSportMeta>
                            {selectedSport.primaryName}
                            {selectedSport.parentOptionName
                              ? ` • ${selectedSport.parentOptionName}`
                              : ""}
                          </SelectedSportMeta>
                        </SelectedSportBadge>
                        <SportRemoveButton
                          type="button"
                          onClick={handleClearSport}
                        >
                          Clear selection
                        </SportRemoveButton>
                      </>
                    ) : (
                      <EmptySportState>No sport selected</EmptySportState>
                    )}
                  </SportSelectionInfo>
                  <SportActions>
                    <SportButton
                      type="button"
                      onClick={() => setSportModalOpen(true)}
                    >
                      {selectedSport ? "Change sport" : "Choose sport"}
                    </SportButton>
                  </SportActions>
                </SportSelectionWrapper>
              </Section>
            </Body>
          )}

          {currentStep === 2 && (
            <Body>
              <Section>
                <SectionTitle>Assign to Recipients</SectionTitle>

                <Tabs>
                  <TabButton
                    type="button"
                    aria-pressed={activeTab === "individual"}
                    active={activeTab === "individual"}
                    onClick={() => {
                      setActiveTab("individual");
                      setSearchTerm("");
                      setSelectedCategories([]);
                    }}
                  >
                    Individual Selection
                  </TabButton>
                  <TabButton
                    type="button"
                    aria-pressed={activeTab === "category"}
                    active={activeTab === "category"}
                    onClick={() => {
                      setActiveTab("category");
                      setSearchTerm("");
                    }}
                  >
                    By Category
                  </TabButton>
                </Tabs>

                <SelectionLayout>
                  <LeftPanel>
                    {activeTab === "individual" && (
                      <SelectionBody>
                        <SearchBar>
                          <SearchField
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search recipients by name or email..."
                          />
                          <Hint>
                            {searchTerm
                              ? `${filteredRecipients.length} recipient${filteredRecipients.length !== 1 ? "s" : ""} found`
                              : `${availableRecipients.length} available recipients`}
                          </Hint>
                        </SearchBar>

                        {searchTerm ? (
                          <List>
                            {filteredRecipients.map((r) => (
                              <ListItem key={r.id} selected={selectedRecipients.includes(r.id)} onClick={() => toggleRecipient(r.id)}>
                                <Checkbox type="checkbox" checked={selectedRecipients.includes(r.id)} onChange={(e) => { e.stopPropagation(); toggleRecipient(r.id); }} onClick={(e) => e.stopPropagation()} />
                                <ItemDetails>
                                  <ItemName>{r.first_name} {r.last_name}</ItemName>
                                  <ItemSub>{r.email}</ItemSub>
                                </ItemDetails>
                              </ListItem>
                            ))}
                            {filteredRecipients.length === 0 && (
                              <Empty>
                                <EmptyTitle>No recipients found</EmptyTitle>
                                <EmptyText>Try adjusting your search terms</EmptyText>
                              </Empty>
                            )}
                          </List>
                        ) : (
                          <Empty>
                            <EmptyTitle>Search for recipients</EmptyTitle>
                            <EmptyText>Start typing to find available recipients by name or email</EmptyText>
                          </Empty>
                        )}
                      </SelectionBody>
                    )}

                    {activeTab === "category" && (
                      <SelectionBody>
                        {selectedCategories.length > 0 && (
                          <Banner>
                            <BannerInfo>
                              {addableRecipients.length > 0
                                ? `${addableRecipients.length} new recipient${
                                    addableRecipients.length !== 1 ? "s" : ""
                                  } ready to add${
                                    filteredRecipients.length > addableRecipients.length
                                      ? ` (${filteredRecipients.length - addableRecipients.length} already selected)`
                                      : ""
                                  }`
                                : filteredRecipients.length > 0
                                ? "Everyone matching this selection is already selected"
                                : "No recipients match this selection"}
                            </BannerInfo>
                            <BannerBtn
                              type="button"
                              onClick={addCategoryRecipients}
                              disabled={addableRecipients.length === 0}
                            >
                              Add {addableRecipients.length} Recipient
                              {addableRecipients.length !== 1 ? "s" : ""}
                            </BannerBtn>
                          </Banner>
                        )}

                        {filterCategories.length > 0 ? (
                          <CatList>
                            {filterCategories.map((category) => (
                              <CatGroup key={category.id}>
                                <CatHeader onClick={() => toggleExpand(category.id)}>
                                  <CatHeaderLeft>
                                    <Caret>{expandedCategory === category.id ? "▼" : "▶"}</Caret>
                                    <CatName>{category.name}</CatName>
                                  </CatHeaderLeft>
                                  <CatCount>
                                    {category.options.filter((o) => selectedCategories.includes(o.id)).length} of {category.options.length}
                                  </CatCount>
                                </CatHeader>
                                {expandedCategory === category.id && (
                                  <CatOptions>
                                    {category.options.map((o) => (
                                      <CatOption key={o.id} selected={selectedCategories.includes(o.id)} onClick={() => toggleCategory(o.id)}>
                                        <Checkbox type="checkbox" checked={selectedCategories.includes(o.id)} readOnly />
                                        <CatOptionText>{o.name}</CatOptionText>
                                      </CatOption>
                                    ))}
                                  </CatOptions>
                                )}
                              </CatGroup>
                            ))}
                          </CatList>
                        ) : (
                          <Empty>
                            <EmptyTitle>No categories available</EmptyTitle>
                            <EmptyText>Contact your administrator to set up recipient categories</EmptyText>
                          </Empty>
                        )}
                      </SelectionBody>
                    )}
                  </LeftPanel>

                  <RightPanel>
                    <RightHeader>
                      <RightTitle>Selected Recipients ({selectedRecipients.length})</RightTitle>
                      {selectedRecipients.length > 0 && (
                        <Clear type="button" onClick={clearAllRecipients}>Clear All</Clear>
                      )}
                    </RightHeader>

                    {selectedRecipients.length > 0 ? (
                      <RightList>
                        {selectedRecipients.map((id) => {
                          const r = availableRecipients.find((x) => x.id === id);
                          return r ? (
                            <RightCard key={id}>
                              <RightDetails>
                                <RightName>{r.first_name} {r.last_name}</RightName>
                                <RightSub>{r.email}</RightSub>
                                {r.role_name && <RightRole>{r.role_name}</RightRole>}
                              </RightDetails>
                              <Remove type="button" onClick={() => toggleRecipient(id)}>×</Remove>
                            </RightCard>
                          ) : null;
                        })}
                      </RightList>
                    ) : (
                      <RightEmpty>
                        <EmptyTitle>No recipients selected</EmptyTitle>
                        <EmptyText>Use the selection options to choose recipients</EmptyText>
                      </RightEmpty>
                    )}
                  </RightPanel>
                </SelectionLayout>
              </Section>
            </Body>
          )}

          {currentStep === 3 && (
            <Body>
              <Section>
                <SectionTitle>Email Content</SectionTitle>
                <Field>
                  <Label>Subject (English) *</Label>
                  <Input
                    type="text"
                    value={emailContent.subjectEn}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmailContent((p) => ({ ...p, subjectEn: v }));
                      updateFormData({ emailSubject: v });
                    }}
                    placeholder="You're invited to take a brief survey"
                  />
                </Field>
                <Field>
                  <Label>Body (English) *</Label>
                  <TextArea
                    rows={8}
                    value={emailContent.bodyEn}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmailContent((p) => ({ ...p, bodyEn: v }));
                      updateFormData({ emailBody: v });
                    }}
                    placeholder="Hello, we'd appreciate your input..."
                  />
                </Field>
                <Divider />
                <Field>
                  <FieldHeader>
                    <Label>Body (French) *</Label>
                    <TemplateButton
                      type="button"
                      onClick={handleApplyDefaultFrenchEmail}
                    >
                      Utiliser le modèle par défaut
                    </TemplateButton>
                  </FieldHeader>
                  <TextArea
                    rows={8}
                    value={emailContent.bodyFr}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmailContent((p) => ({ ...p, bodyFr: v }));
                      updateFormData({ emailBodyFr: v });
                    }}
                    placeholder="Bonjour, nous apprécierions vos commentaires..."
                  />
                </Field>
              </Section>
            </Body>
          )}

          <Footer>
            <div>
              {currentStep > 1 ? (
                <Secondary type="button" onClick={goBack}>Back</Secondary>
              ) : (
                <span />
              )}
            </div>
            <div>
              {currentStep < 3 ? (
                <Primary type="button" onClick={goNext}>Continue</Primary>
              ) : (
                <Primary type="submit" disabled={!canSubmit || loading}>
                  {loading && <Spinner aria-hidden="true" />}
                  {loading ? "Sending…" : "Send Pulse"}
                </Primary>
              )}
            </div>
          </Footer>
        </form>
      </Card>
      <Modal
        open={sportModalOpen}
        onClose={() => {
          setSportModalOpen(false);
          setSportSearchTerm("");
        }}
        closeIcon
        size="large"
      >
        <Modal.Header>Select Sport</Modal.Header>
        <Modal.Content scrolling>
          <SportModalContent>
            <SportModalIntro>
              Choose the sport subfactor to associate with this pulse. You can
              search across all primary categories.
            </SportModalIntro>
            {sportSubfactorStructure.length > 0 ? (
              <>
                <SportSearchInput
                  type="text"
                  value={sportSearchTerm}
                  onChange={(e) => setSportSearchTerm(e.target.value)}
                  placeholder="Search by sport, parent, or primary category..."
                />
                <SportStructureWrapper>
                  {filteredSportStructure.length > 0 ? (
                    filteredSportStructure.map((primary) => (
                      <SportPrimaryBlock
                        key={`primary-${primary.primaryId}`}
                      >
                        <SportPrimaryTitle>
                          {primary.primaryName}
                        </SportPrimaryTitle>
                        {primary.parents.map((parent) => (
                          <SportParentBlock
                            key={`parent-${primary.primaryId}-${parent.parentOptionId}`}
                          >
                            <SportParentTitle>
                              {parent.parentOptionName}
                            </SportParentTitle>
                            <SportOptionList>
                              {parent.subfactors.map((subfactor) => {
                                const isSelected =
                                  selectedSport?.id === subfactor.id &&
                                  selectedSport?.parentOptionId ===
                                    parent.parentOptionId &&
                                  selectedSport?.primaryId === primary.primaryId;
                                return (
                                  <SportOptionTag
                                    key={`subfactor-${subfactor.id}`}
                                    type="button"
                                    selected={isSelected}
                                    onClick={() =>
                                      handleSportSelect(subfactor, parent, primary)
                                    }
                                  >
                                    {subfactor.name}
                                  </SportOptionTag>
                                );
                              })}
                            </SportOptionList>
                          </SportParentBlock>
                        ))}
                      </SportPrimaryBlock>
                    ))
                  ) : (
                    <SportEmptyState>
                      No sports match your search. Try a different term.
                    </SportEmptyState>
                  )}
                </SportStructureWrapper>
              </>
            ) : (
              <SportEmptyState>
                Sport subfactors are not available in this survey structure.
              </SportEmptyState>
            )}
          </SportModalContent>
        </Modal.Content>
        <Modal.Actions>
          <SportCloseButton type="button" onClick={() => setSportModalOpen(false)}>
            Done
          </SportCloseButton>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

const SurveyFormPageWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialSurveyData = location.state?.initialSurveyData || null;
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    if (!initialSurveyData) {
      navigate("/app/otp/AI/pulse-survey", { replace: true });
    }
  }, [initialSurveyData, navigate]);

  if (!initialSurveyData) return null;

  return (
    <UserSearchProvider>
      <SurveyProvider initialFormData={initialSurveyData} mode="create">
        <SurveyFormPageInner selectedPulse={initialSurveyData} />
      </SurveyProvider>
    </UserSearchProvider>
  );
};

export default SurveyFormPageWrapper;

// Styles
const Container = styled.div`
  padding: 16px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const Breadcrumbs = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const CrumbLink = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    color: #414141;
    background-color: #f3f4f6;
  }
`;

const CrumbSeparator = styled.span`
  color: #c0c0c0;
  font-size: 12px;
`;

const CrumbCurrent = styled.span`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
`;

const HeaderText = styled.div``;
const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 22px;
  color: #414141;
  margin: 0 0 4px 0;
`;
const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
`;

const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;
const StepNum = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  ${(props) => {
    if (props.completed) return `background: #2e7d2e; color: white; border: 2px solid #2e7d2e;`;
    if (props.active) return `background: #476dfa; color: white; border: 2px solid #476dfa;`;
    return `background: #f0f0f0; color: #999; border: 2px solid #e0e0e0;`;
  }}
`;
const StepText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  color: #7e7e7e;
`;
const Connector = styled.div`
  width: 36px;
  height: 2px;
  background: ${(props) => (props.completed ? "#2e7d2e" : "#e0e0e0")};
`;

const Card = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  overflow: hidden;
`;
const PulseInfo = styled.div`
  margin-bottom: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 14px;
  background: #f8f9fa;
`;
const PulseTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;
const PulseDesc = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #666;
`;
const Body = styled.div`
  padding: 24px;
`;
const Section = styled.div`
  margin-bottom: 24px;
`;
const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const SportSelectionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const SportSelectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-height: 40px;
`;

const SelectedSportBadge = styled.div`
  background-color: #ebf1fd;
  color: #2d70e2;
  border-radius: 20px;
  padding: 10px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
`;

const SelectedSportName = styled.span`
  font-family: "Raleway";
  font-weight: 600;
  font-size: 14px;
`;

const SelectedSportMeta = styled.span`
  font-family: "Raleway";
  font-weight: 500;
  font-size: 12px;
  color: #2a3039;
  opacity: 0.7;
`;

const SportRemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #f15062;
  font-family: "Raleway";
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
`;

const EmptySportState = styled.span`
  font-family: "Raleway";
  font-size: 14px;
  color: #666d79;
`;

const SportActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SportButton = styled.button`
  background: #2d70e2;
  border: none;
  color: #ffffff;
  border-radius: 8px;
  padding: 10px 18px;
  font-family: "Raleway";
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #1f5acc;
  }
`;

const SportModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SportModalIntro = styled.p`
  font-family: "Raleway";
  font-size: 14px;
  color: #2a3039;
  margin: 0;
`;

const SportSearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d9dce3;
  font-family: "Raleway";
  font-size: 14px;
`;

const SportStructureWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SportPrimaryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SportPrimaryTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #2a3039;
  margin: 0;
`;

const SportParentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SportParentTitle = styled.h4`
  font-family: "Raleway";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin: 0;
`;

const SportOptionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SportOptionTag = styled.button`
  border: 1px solid ${(props) => (props.selected ? "#2d70e2" : "#d9dce3")};
  background: ${(props) => (props.selected ? "#ebf1fd" : "#ffffff")};
  color: ${(props) => (props.selected ? "#2d70e2" : "#2a3039")};
  border-radius: 20px;
  padding: 8px 14px;
  font-family: "Raleway";
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #2d70e2;
  }
`;

const SportEmptyState = styled.div`
  font-family: "Raleway";
  font-size: 14px;
  color: #666d79;
  padding: 24px;
  text-align: center;
  background: #f8f9fb;
  border-radius: 12px;
`;

const SportCloseButton = styled.button`
  background: #2d70e2;
  border: none;
  color: #ffffff;
  border-radius: 8px;
  padding: 10px 18px;
  font-family: "Raleway";
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #1f5acc;
  }
`;
const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;
const Label = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 6px;
`;
const FieldHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;

  ${Label} {
    margin-bottom: 0;
  }
`;
const TemplateButton = styled.button`
  background: transparent;
  border: none;
  color: #2d70e2;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:hover {
    color: #1f5acc;
  }
`;
const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #476dfa; }
`;
const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;
  resize: vertical;
  &:focus { outline: none; border-color: #476dfa; }
`;
const Divider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 16px 0;
`;

// Recipients selection
const Tabs = styled.div`
  display: flex;
  gap: 2px;
  background: #f8f9fa;
  padding: 4px;
  border-radius: 8px;
  width: fit-content;
  margin-bottom: 16px;
`;
const TabButton = styled.button`
  padding: 8px 16px;
  background: ${(p) => (p.active ? "#476dfa" : "transparent")};
  border: none;
  border-radius: 6px;
  color: ${(p) => (p.active ? "white" : "#7e7e7e")};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  &:hover { background: ${(p) => (p.active ? "#3d5ce6" : "#e9ecef")}; }
`;

const SelectionLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 24px;
  min-height: 420px;
`;
const LeftPanel = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;
const SelectionBody = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const SearchBar = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
`;
const SearchField = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #476dfa; }
`;
const Hint = styled.div`
  font-family: "Poppins";
  font-size: 11px;
  color: #7e7e7e;
  padding-top: 6px;
`;
const List = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;
const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
  border: 1px solid ${(p) => (p.selected ? "#476dfa" : "transparent")};
  &:hover { background: ${(p) => (p.selected ? "#f0f4ff" : "#f8f9fa")}; }
`;
const Checkbox = styled.input`
  margin: 0;
  cursor: pointer;
`;
const ItemDetails = styled.div`
  flex: 1;
`;
const ItemName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 2px;
`;
const ItemSub = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;
const Empty = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;
const EmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin: 0 0 6px 0;
`;
const EmptyText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin: 0;
`;

// Category UI
const Banner = styled.div`
  padding: 12px 16px;
  background: #f0f4ff;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const BannerInfo = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #476dfa;
  font-weight: 500;
`;
const BannerBtn = styled.button`
  padding: 6px 12px;
  background: #476dfa;
  border: none;
  border-radius: 4px;
  color: white;
  font-family: "Poppins";
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover { background: #3d5ce6; }

  &:disabled {
    background: #a7b8ff;
    cursor: not-allowed;
    opacity: 0.85;
  }

  &:disabled:hover {
    background: #a7b8ff;
  }
`;
const CatList = styled.div`
  flex: 1;
  overflow-y: auto;
`;
const CatGroup = styled.div`
  border-bottom: 1px solid #f0f0f0;
`;
const CatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover { background: #f8f9fa; }
`;
const CatHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Caret = styled.span`
  font-size: 12px;
  color: #7e7e7e;
`;
const CatName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
`;
const CatCount = styled.div`
  font-family: "Poppins";
  font-size: 11px;
  color: #7e7e7e;
`;
const CatOptions = styled.div`
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
`;
const CatOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 8px 40px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-left: 2px solid ${(p) => (p.selected ? "#476dfa" : "transparent")};
  &:hover { background: #f0f0f0; }
`;
const CatOptionText = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #414141;
`;

// Right panel (selected)
const RightPanel = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const RightHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;
const RightTitle = styled.h4`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin: 0;
`;
const Clear = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  font-family: "Poppins";
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  &:hover { background: #fff5f5; }
`;
const RightList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;
const RightCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 6px;
  background: white;
  transition: all 0.2s;
  &:hover { border-color: #476dfa; box-shadow: 0 2px 4px rgba(71, 109, 250, 0.1); }
`;
const RightDetails = styled.div`
  flex: 1;
`;
const RightName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 2px;
`;
const RightSub = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;
const RightRole = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #7e7e7e;
  background: #f0f4ff;
  padding: 1px 6px;
  border-radius: 3px;
  display: inline-block;
  margin-top: 2px;
`;
const Remove = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
  &:hover { background: #fff5f5; }
`;
const RightEmpty = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

// Footer
const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;
const Secondary = styled.button`
  padding: 10px 16px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #f8f9fa; border-color: #476dfa; color: #476dfa; }
`;
const Primary = styled.button`
  padding: 10px 20px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
  &:hover:not(:disabled) { background: #3d5ce6; }
  &:disabled { background: #cccccc; cursor: not-allowed; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  animation: ${spin} 0.8s linear infinite;
`;
